import { parse } from "query-string";

import { wikiArticleExperiencePhotoUrl, wikiArticlePhotoUrl } from "@/mk2/apps/wiki/urls";
import { url } from "@/mk2/helpers/urls";
import { ExperienceEntity, PhotoEntity, WikiArticleEntity } from "@/mk2/schemas";

// Query flag put on lightbox links that originate from the merged product gallery
// (the top PhotosRowOfSquares). Its presence switches the photo detail pages into
// "merged" navigation (across article + all approved experience photos); without it
// they stay scoped to their own source (article photos, or a single experience).
export const GALLERY_SCOPE_PARAM = "g";
export const GALLERY_SCOPE_MERGED = "all";

export interface ArticleGalleryItem {
    photofile: PhotoEntity;
    experienceId: number | null; // null => article (manufacturer) photo
}

export interface ArticleGalleryItemOrigin {
    isReviewArticlePhotoGalleryItem: boolean;
    nick?: string;
    experienceId?: number;
}

// True when a location's query string carries the merged-gallery scope flag (?g=all).
export function isMergedScope(search: string): boolean {
    return parse(search)[GALLERY_SCOPE_PARAM] === GALLERY_SCOPE_MERGED;
}

// Ordered, deduped merged gallery shown on review product pages: ALL article photos
// first (cover included, experienceId=null), then photos of approved && !deleted
// experiences in article.experiences order, deduped by photofile.id (article photo
// wins; on cross-experience dupes the first experience wins). Experience photos are
// only merged when experiencesAllowed — same gate the product row uses — so the row
// and the merged lightbox stay in lockstep. Degrades to article.photos otherwise.
export function buildArticleGalleryPhotos(
    article: Pick<WikiArticleEntity, "photos" | "experiences" | "experiencesAllowed">,
): ArticleGalleryItem[] {
    const items: ArticleGalleryItem[] = (article.photos ?? []).map((photofile) => ({
        photofile,
        experienceId: null,
    }));

    if (!article.experiencesAllowed) {
        return items;
    }

    const seenPhotoIds = new Set(items.map((item) => item.photofile.id));
    for (const experience of article.experiences ?? []) {
        if (!experience.approved || experience.deleted) {
            continue;
        }
        for (const experiencePhoto of experience.photos ?? []) {
            const photofile = experiencePhoto.photofile;
            if (seenPhotoIds.has(photofile.id)) {
                continue;
            }
            seenPhotoIds.add(photofile.id);
            items.push({ photofile, experienceId: experience.id });
        }
    }

    return items;
}

// Detail URL for a merged-gallery item, routed by its origin and carrying the merged
// scope flag so prev/next keeps spanning the whole gallery.
export function galleryItemLinkTo(
    article: Pick<WikiArticleEntity, "category" | "slug">,
    item: ArticleGalleryItem,
): string {
    const mergedScope = { [GALLERY_SCOPE_PARAM]: GALLERY_SCOPE_MERGED };
    if (item.experienceId !== null) {
        return url(
            wikiArticleExperiencePhotoUrl,
            {
                categorySlug: article.category.slug,
                articleSlug: article.slug,
                experienceId: item.experienceId,
                photoId: item.photofile.id,
            },
            mergedScope,
        );
    }
    return url(
        wikiArticlePhotoUrl,
        {
            categorySlug: article.category.slug,
            articleSlug: article.slug,
            photoId: item.photofile.id,
        },
        mergedScope,
    );
}

// Author nickname of an experience, handling guests: guests have a free-text
// authorNickname, registered reviewers fall through to their username. Matches the
// precedence in wiki/structuredData.ts. May be undefined if neither is present.
export function experienceAuthorNick(
    experience: Pick<ExperienceEntity, "authorNickname" | "authorUser">,
): string | undefined {
    return experience.authorNickname || experience.authorUser?.username;
}

// Origin descriptor for the lightbox label: manufacturer photo vs. photo from a review
// (with the reviewer's nickname and the experience id to link back to the review).
export function galleryItemOrigin(
    article: Pick<WikiArticleEntity, "experiences">,
    item: ArticleGalleryItem,
): ArticleGalleryItemOrigin {
    if (item.experienceId === null) {
        return { isReviewArticlePhotoGalleryItem: true };
    }
    const experience = (article.experiences ?? []).find((e) => e.id === item.experienceId);
    return {
        isReviewArticlePhotoGalleryItem: false,
        nick: experience ? experienceAuthorNick(experience) : undefined,
        experienceId: item.experienceId,
    };
}
